import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Descope } from '@descope/react-sdk';

import { Loader } from '@/components/loader/loader';
import { useDescopeFlow } from '@/hooks/use-descope-flow';
import { notify } from '@/ui/snackbar/notify';
import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { captureDescopeFlowError } from '@/auth/helpers/capture-descope-flow-error';
import { createDescopeErrorMessageTransformer } from '@/auth/helpers/descope-error-message-transformer';
import { handleDescopeSignInSuccess } from '@/auth/helpers/handle-descope-sign-in-success';
import { useSignIn } from '@/auth/hooks/use-sign-in.mutation';
import type {
  SignInFlowSuccessEvent,
  DescopeFlowErrorEvent,
} from '@/auth/types';

export const SignInPage = () => {
  const { t } = useTranslation('auth');
  const { flowKey, isFlowLoading, onReady } = useDescopeFlow();

  const { handleSignIn } = useSignIn();

  const errorTransformer = useMemo(
    () => createDescopeErrorMessageTransformer(t),
    [t],
  );

  const handleSuccessfulSignIn = async (event: SignInFlowSuccessEvent) => {
    await handleDescopeSignInSuccess({
      onMissingSessionJwt: () => {
        notify({ message: t('descopeErrors.oauthSignInFailed') });
      },
      onSignIn: descopeToken => handleSignIn({ descopeToken }),
      sessionJwt: event.detail.sessionJwt,
    });
  };

  const handleError = (event: string | Event | DescopeFlowErrorEvent) => {
    captureDescopeFlowError('sign-in', event);

    notify({ message: t`loginError` });
  };

  return (
    <WithHtmlSavvyTitle title={t`signUpPageTitle`}>
      {isFlowLoading && <Loader />}
      <Descope
        key={flowKey}
        flowId={window.runTimeEnvs.VITE_DESCOPE_SIGN_IN_FLOW}
        onSuccess={handleSuccessfulSignIn}
        onError={handleError}
        errorTransformer={errorTransformer}
        onReady={onReady}
      />
    </WithHtmlSavvyTitle>
  );
};
